import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IconArrowsExchange, IconUserPlus, IconCheck, IconPaperclip, IconDownload, IconAlertTriangle } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import { encodePathSegments } from '../../../utils/media-url';
import CustomFieldInput from '../authentication/configuracoes/shared/CustomFieldInput';
import { isFieldVisible } from '../authentication/configuracoes/shared/fieldVisibility';
import EntityPickerInline from './EntityPickerInline';
import TicketTimelineItem from './TicketTimelineItem';
import RelatedTicketsSection from './RelatedTicketsSection';
import SgqSection from './SgqSection';
import { PRIORITY_COLORS, PRIORITY_LABELS, STATUS_COLORS, STATUS_LABELS, formatTicketDate, shortTicketId } from './ticketUtils';

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:8000/api/';

const chipSelectSx = (colors) => ({
    height: 28,
    fontWeight: 600,
    textTransform: 'none',
    bgcolor: colors?.bg,
    color: colors?.color,
    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0 }
});

// Converte um ISO datetime (backend) pro formato que <input type="datetime-local">
// espera (sem timezone, "YYYY-MM-DDTHH:mm") e vice-versa.
const toDatetimeLocal = (iso) => (iso ? iso.slice(0, 16) : '');

// Detalhe completo do Ticket - reutilizável a partir da listagem, da
// Conversation (DadosDealDrawer.js) e do Deal (DealDrawer.js), sempre via
// `ticketId` (busca os dados sozinho, não recebe o objeto pronto - assim
// funciona igual não importa de onde foi aberto).
const TicketDetailDrawer = ({ appId, ticketId, onClose, onChanged }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const history = useHistory();

    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [saving, setSaving] = useState(false);

    const [title, setTitle] = useState('');
    const [newComment, setNewComment] = useState('');
    const [postingComment, setPostingComment] = useState(false);

    const [teams, setTeams] = useState([]);
    const [transferAnchor, setTransferAnchor] = useState(null);
    const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);

    const [tagInput, setTagInput] = useState('');

    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [customFieldValues, setCustomFieldValues] = useState({});
    const [savingFields, setSavingFields] = useState(false);

    const [attachments, setAttachments] = useState([]);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);

    const load = async () => {
        if (!appId || !ticketId) return;
        setLoading(true);
        try {
            const response = await api.get(`v1/apps/${appId}/tickets/${ticketId}/`);
            setTicket(response.data);
            setTitle(response.data.title || '');
            const values = {};
            (response.data.field_values || []).forEach((fv) => {
                values[fv.field_name] = fv.value;
            });
            setCustomFieldValues(values);
        } catch (error) {
            console.error('Erro ao carregar ticket', error);
            setStatus({ type: 'error', message: 'Erro ao carregar Ticket.' });
        } finally {
            setLoading(false);
        }
    };

    const loadAttachments = async () => {
        if (!appId || !ticketId) return;
        try {
            const response = await api.get(`v1/apps/${appId}/documents/?ticket=${ticketId}`);
            setAttachments(response.data?.documents || []);
        } catch (error) {
            console.error('Erro ao carregar anexos', error);
        }
    };

    useEffect(() => {
        load();
        loadAttachments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, ticketId]);

    useEffect(() => {
        if (!appId) return;
        api
            .get(`v1/apps/${appId}/teams/`)
            .then((response) => setTeams(response.data?.teams || []))
            .catch((error) => console.error('Erro ao carregar equipes', error));
        api
            .get(`v1/apps/${appId}/ticket_fields/`)
            .then((response) => setFieldsConfig(response.data || []))
            .catch((error) => console.error('Erro ao carregar campos customizados', error));
    }, [appId]);

    const notifyChanged = () => {
        if (onChanged) onChanged();
    };

    const patchTicket = async (payload) => {
        setSaving(true);
        try {
            const response = await api.patch(`v1/apps/${appId}/tickets/${ticketId}/`, payload);
            setTicket(response.data);
            notifyChanged();
        } catch (error) {
            console.error('Erro ao atualizar ticket', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao atualizar Ticket.' });
        } finally {
            setSaving(false);
        }
    };

    const handleTitleBlur = () => {
        if (title.trim() && title !== ticket?.title) {
            patchTicket({ title });
        }
    };

    const handleAssume = async () => {
        setSaving(true);
        setStatus({ type: '', message: '' });
        try {
            const response = await api.post(`v1/apps/${appId}/tickets/${ticketId}/assume/`);
            setTicket(response.data);
            notifyChanged();
        } catch (error) {
            console.error('Erro ao assumir ticket', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao assumir o Ticket.' });
        } finally {
            setSaving(false);
        }
    };

    const handleTransfer = async (teamId) => {
        setTransferAnchor(null);
        setSaving(true);
        setStatus({ type: '', message: '' });
        try {
            const response = await api.post(`v1/apps/${appId}/tickets/${ticketId}/transfer/`, { team: teamId });
            setTicket(response.data);
            notifyChanged();
        } catch (error) {
            console.error('Erro ao transferir ticket', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao transferir o Ticket.' });
        } finally {
            setSaving(false);
        }
    };

    const handleSelectAssignee = (user) => {
        setAssigneeMenuOpen(false);
        patchTicket({ assigned_to: user ? user.id : null });
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        setPostingComment(true);
        try {
            await api.post(`v1/apps/${appId}/tickets/${ticketId}/comments/`, { message: newComment, internal: true });
            setNewComment('');
            await load();
            notifyChanged();
        } catch (error) {
            console.error('Erro ao comentar', error);
            setStatus({ type: 'error', message: 'Erro ao adicionar comentário.' });
        } finally {
            setPostingComment(false);
        }
    };

    const addTag = () => {
        const value = tagInput.trim();
        if (!value || (ticket.tags || []).includes(value)) return;
        patchTicket({ tags: [...(ticket.tags || []), value] });
        setTagInput('');
    };

    const removeTag = (value) => {
        patchTicket({ tags: (ticket.tags || []).filter((t) => t !== value) });
    };

    const handleSaveCustomFields = async () => {
        setSavingFields(true);
        try {
            const response = await api.patch(`v1/apps/${appId}/tickets/${ticketId}/`, { fields: customFieldValues });
            setTicket(response.data);
            setStatus({ type: 'success', message: 'Campos customizados salvos.' });
        } catch (error) {
            console.error('Erro ao salvar campos customizados', error);
            setStatus({ type: 'error', message: 'Erro ao salvar campos customizados.' });
        } finally {
            setSavingFields(false);
        }
    };

    const handleUploadAttachment = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingAttachment(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('ticket', ticketId);
            await api.post(`v1/apps/${appId}/documents/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            await loadAttachments();
        } catch (error) {
            console.error('Erro ao enviar anexo', error);
            setStatus({ type: 'error', message: 'Erro ao enviar anexo.' });
        } finally {
            setUploadingAttachment(false);
            e.target.value = '';
        }
    };

    const getAttachmentUrl = (doc) => `${BACKEND_SERVER}v1/apps/${appId}/media/${encodePathSegments(doc.r2_key)}`;

    const timeline = useMemo(() => {
        if (!ticket) return [];
        const comments = (ticket.comments || []).map((c) => ({ ...c, kind: 'comment' }));
        const events = (ticket.events || []).map((e) => ({ ...e, kind: 'event' }));
        return [...comments, ...events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }, [ticket]);

    const openConversation = () => {
        if (ticket?.conversation) history.push(`/dashboard/chats?id=${ticket.conversation}`);
    };

    if (!ticketId) return null;

    return (
        <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: '70vw' }, p: 0 } }}>
            {loading || !ticket ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* ===== CABEÇALHO ===== */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: `1px solid ${isDark ? '#2a3942' : '#e0e0e0'}`,
                            backgroundColor: isDark ? '#111b21' : '#f8f9fa'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: isDark ? '#8696a0' : '#667781', mb: 0.5 }}>
                                    {shortTicketId(ticket.id)}
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="standard"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    onBlur={handleTitleBlur}
                                    InputProps={{ sx: { fontSize: 22, fontWeight: 600, color: isDark ? '#e9edef' : '#111' } }}
                                />
                            </Box>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {status.message && (
                            <Alert severity={status.type} sx={{ mt: 1 }} onClose={() => setStatus({ type: '', message: '' })}>
                                {status.message}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mt: 2 }}>
                            {ticket.is_overdue && (
                                <Chip
                                    icon={<IconAlertTriangle size={15} />}
                                    label="Vencido"
                                    size="small"
                                    sx={{ fontWeight: 700, bgcolor: '#FBD5D5', color: '#B42121' }}
                                />
                            )}

                            {/* Status */}
                            <TextField
                                select
                                size="small"
                                value={ticket.status}
                                onChange={(e) => patchTicket({ status: e.target.value })}
                                sx={{ minWidth: 150, '& .MuiOutlinedInput-root': chipSelectSx(STATUS_COLORS[ticket.status]) }}
                            >
                                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* Prioridade */}
                            <TextField
                                select
                                size="small"
                                value={ticket.priority}
                                onChange={(e) => patchTicket({ priority: e.target.value })}
                                sx={{ minWidth: 130, '& .MuiOutlinedInput-root': chipSelectSx(PRIORITY_COLORS[ticket.priority]) }}
                            >
                                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {/* Equipe */}
                            <Chip
                                icon={<IconArrowsExchange size={15} />}
                                label={`Equipe: ${ticket.team_detail?.name || 'Nenhuma'}`}
                                onClick={(e) => setTransferAnchor(e.currentTarget)}
                                sx={{ fontWeight: 600 }}
                            />
                            <Menu anchorEl={transferAnchor} open={!!transferAnchor} onClose={() => setTransferAnchor(null)}>
                                {teams.length === 0 && <MenuItem disabled>Nenhuma equipe cadastrada</MenuItem>}
                                {teams.map((t) => (
                                    <MenuItem key={t.id} onClick={() => handleTransfer(t.id)}>
                                        {t.name}
                                    </MenuItem>
                                ))}
                            </Menu>

                            {/* Responsável */}
                            <Chip
                                icon={<IconUserPlus size={15} />}
                                label={`Responsável: ${
                                    ticket.assigned_to_detail
                                        ? `${ticket.assigned_to_detail.first_name || ''} ${ticket.assigned_to_detail.last_name || ''}`.trim() ||
                                          ticket.assigned_to_detail.username
                                        : 'Nenhum'
                                }`}
                                onClick={() => setAssigneeMenuOpen((v) => !v)}
                                sx={{ fontWeight: 600 }}
                            />

                            {!ticket.assigned_to && (
                                <Button size="small" variant="outlined" startIcon={<IconCheck size={15} />} onClick={handleAssume} disabled={saving}>
                                    Assumir
                                </Button>
                            )}

                            {ticket.status !== 'resolved' && ticket.status !== 'closed' ? (
                                <Button size="small" variant="text" onClick={() => patchTicket({ status: 'resolved' })} disabled={saving}>
                                    Resolver
                                </Button>
                            ) : (
                                <Button size="small" variant="text" onClick={() => patchTicket({ status: 'open' })} disabled={saving}>
                                    Reabrir
                                </Button>
                            )}
                        </Box>

                        {assigneeMenuOpen && (
                            <Box sx={{ mt: 1 }}>
                                <EntityPickerInline
                                    label="Buscar responsável"
                                    placeholder="Buscar usuário por nome ou e-mail..."
                                    searchUrl={(term) => `users/?search=${term}`}
                                    extractResults={(data) => data}
                                    getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`}
                                    value={null}
                                    onSelect={handleSelectAssignee}
                                    onClear={() => handleSelectAssignee(null)}
                                    clearLabel="Sem responsável"
                                />
                            </Box>
                        )}
                    </Box>

                    {/* ===== CORPO: TIMELINE + CONTEXTO ===== */}
                    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                        {/* Área principal */}
                        <Box sx={{ flex: 2, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                {ticket.description || 'Sem descrição.'}
                            </Typography>

                            {fieldsConfig.length > 0 && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        Campos customizados
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {fieldsConfig
                                            .filter((f) => isFieldVisible(f, fieldsConfig, customFieldValues))
                                            .map((f) => (
                                                <CustomFieldInput
                                                    key={f.id}
                                                    field={f}
                                                    value={customFieldValues[f.name]}
                                                    onChange={(value) => setCustomFieldValues({ ...customFieldValues, [f.name]: value })}
                                                />
                                            ))}
                                    </Grid>
                                    <Box>
                                        <Button size="small" variant="outlined" onClick={handleSaveCustomFields} disabled={savingFields}>
                                            {savingFields ? <CircularProgress size={16} /> : 'Salvar campos customizados'}
                                        </Button>
                                    </Box>
                                </>
                            )}

                            <Divider sx={{ my: 1 }} />

                            <Typography variant="subtitle2" fontWeight={700}>
                                Timeline
                            </Typography>

                            {timeline.length === 0 && (
                                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                    Nenhuma atividade ainda.
                                </Typography>
                            )}

                            {timeline.map((item) => (
                                <TicketTimelineItem key={`${item.kind}-${item.id}`} item={item} />
                            ))}

                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    placeholder="Adicionar comentário interno..."
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Button variant="contained" onClick={handleAddComment} disabled={postingComment || !newComment.trim()}>
                                        {postingComment ? <CircularProgress size={18} /> : 'Comentar'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        {/* Painel de contexto */}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 280,
                                maxWidth: 340,
                                borderLeft: `1px solid ${isDark ? '#2a3942' : '#e0e0e0'}`,
                                overflowY: 'auto',
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}
                        >
                            {/* Cliente */}
                            <Box sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#fff' }}>
                                <Typography fontWeight={600}>Cliente</Typography>
                                {ticket.contact_detail ? (
                                    <>
                                        <Typography variant="body2">
                                            {ticket.contact_detail.first_name} {ticket.contact_detail.last_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            {ticket.contact_detail.email}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                        Nenhum cliente vinculado.
                                    </Typography>
                                )}
                                {ticket.organization_detail && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                        Empresa: {ticket.organization_detail.name}
                                    </Typography>
                                )}
                            </Box>

                            {/* Deal */}
                            <Box sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#fff' }}>
                                <Typography fontWeight={600}>Negócio relacionado</Typography>
                                {ticket.deal_detail ? (
                                    <Box
                                        onClick={() => history.push('/dashboard/config/pipeline')}
                                        sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                    >
                                        <Typography variant="body2">{ticket.deal_detail.title}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            {ticket.deal_detail.stage_name} · R$ {ticket.deal_detail.value}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                        Nenhum negócio vinculado.
                                    </Typography>
                                )}
                            </Box>

                            {/* Conversation */}
                            <Box sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#fff' }}>
                                <Typography fontWeight={600}>Conversa relacionada</Typography>
                                {ticket.conversation_detail ? (
                                    <Box onClick={openConversation} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
                                        <Typography variant="body2">
                                            {ticket.conversation_detail.displayName || 'Conversa'} ({ticket.conversation_detail.type})
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                            Estado: {ticket.conversation_detail.state}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                        Nenhuma conversa vinculada.
                                    </Typography>
                                )}
                            </Box>

                            {/* Prazos (SLA) */}
                            <Box sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#fff' }}>
                                <Typography fontWeight={600}>Prazos</Typography>
                                <TextField
                                    label="Prazo 1ª resposta"
                                    type="datetime-local"
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 1 }}
                                    InputLabelProps={{ shrink: true }}
                                    value={toDatetimeLocal(ticket.first_response_due_at)}
                                    onChange={(e) => patchTicket({ first_response_due_at: e.target.value || null })}
                                />
                                <TextField
                                    label="Prazo de resolução"
                                    type="datetime-local"
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 1 }}
                                    InputLabelProps={{ shrink: true }}
                                    value={toDatetimeLocal(ticket.resolution_due_at)}
                                    onChange={(e) => patchTicket({ resolution_due_at: e.target.value || null })}
                                />
                            </Box>

                            {/* Tags */}
                            <Box sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#fff' }}>
                                <Typography fontWeight={600}>Tags</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 1 }}
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addTag();
                                        }
                                    }}
                                    placeholder="Digite e pressione Enter"
                                />
                                {(ticket.tags || []).length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                        {ticket.tags.map((tag) => (
                                            <Chip key={tag} label={tag} size="small" onDelete={() => removeTag(tag)} />
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {/* Anexos */}
                            <Box sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#fff' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography fontWeight={600}>Anexos</Typography>
                                    <Button component="label" size="small" startIcon={<IconPaperclip size={15} />} disabled={uploadingAttachment}>
                                        {uploadingAttachment ? <CircularProgress size={14} /> : 'Anexar'}
                                        <input hidden type="file" onChange={handleUploadAttachment} />
                                    </Button>
                                </Box>
                                {attachments.length === 0 ? (
                                    <Typography variant="body2" sx={{ opacity: 0.6, mt: 1 }}>
                                        Nenhum anexo ainda.
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                                        {attachments.map((doc) => (
                                            <Box
                                                key={doc.id}
                                                component="a"
                                                href={getAttachmentUrl(doc)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    textDecoration: 'none',
                                                    color: 'inherit',
                                                    '&:hover': { opacity: 0.8 }
                                                }}
                                            >
                                                <IconDownload size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
                                                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {doc.name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {/* Integração SGQ: só renderiza com a flag da conta ligada */}
                            <SgqSection appId={appId} ticket={ticket} onOpened={load} />

                            {/* Outros tickets do mesmo cliente */}
                            {ticket.contact && (
                                <RelatedTicketsSection
                                    appId={appId}
                                    contactId={ticket.contact}
                                    excludeTicketId={ticket.id}
                                    title="Outros tickets deste cliente"
                                    onCreated={notifyChanged}
                                />
                            )}

                            <Typography variant="caption" sx={{ opacity: 0.5 }}>
                                Criado em {formatTicketDate(ticket.created_at)} · Atualizado em {formatTicketDate(ticket.updated_at)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
        </Drawer>
    );
};

export default TicketDetailDrawer;
